import * as DG from "ui/dialogs"
import { createRef, useEffect, useState } from "react"

import {
  DYNAMIC_TAXONOMY_BO_OPTIONS,
  DYNAMIC_TAXONOMY_LI_OPTIONS,
  MATCH_RGX,
  revertInterpolated,
} from "routes/Base/Admin/Taxes/TaxExempt/utils"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation } from "react-query"

import { ApiError } from "api/http/utils"
import Button from "ui/buttons/Button"
import { DialogActions } from "@material-ui/core"
import { EditableInterpolatedText } from "routes/Base/Admin/Taxes/TaxExempt/EditableInterpolatedText"
import { ReactComponent as InfoIcon } from "assets/info-secondary.svg"
import { TaxonomyPattern } from "models/taxonomy"
import { updateTaxonomyPattern } from "api/taxonomies"
import { useAuthenticated } from "providers/A12n"
import { useFeedback } from "components/FeedbackSnackbar"
import { useI18n } from "providers/I18n"

export type ExternalIdPatternModalProps = {
  open: boolean
  onClose: () => void
  updateStatus: (checked: boolean, id: string) => void
  taxonomy?: TaxonomyPattern
  lineItemTaxonomy?: boolean
}

export function ExternalIdPatternModal(props: ExternalIdPatternModalProps) {
  const { open, onClose, taxonomy, updateStatus, lineItemTaxonomy } = props
  const { t } = useI18n()
  const { meta } = useAuthenticated()
  const marketId = meta?.layerId ?? ""
  const id = "external-id-pattern-dialog"
  const sendFeedback = useFeedback((s) => s.send)
  const taxonomyContent = taxonomy?.content || ""
  const [content, setContent] = useState<string>(taxonomyContent)

  const textRef = createRef<HTMLDivElement>()

  const [update] = useMutation(updateTaxonomyPattern, {
    onSuccess: () => {
      queryCache.invalidateQueries(["taxonomy-patterns"])
      onClose()
    },
    onError: (e: ApiError) => {
      const error = e as ApiError
      sendFeedback(error.message, "error")
    },
  })

  const handleClear = () => {
    setContent("")
  }

  const handleChange = (s: string) => {
    const r = !lineItemTaxonomy
      ? revertInterpolated(s, DYNAMIC_TAXONOMY_BO_OPTIONS)
      : revertInterpolated(s, DYNAMIC_TAXONOMY_LI_OPTIONS)
    setContent(r)
  }

  const handleBlur = () => {
    const u = textRef.current?.innerText
    if (u) {
      handleChange(u)
    }
  }

  const onUpdate = (s: string) => {
    handleChange(s)

    update({
      content: s,
      clientAgencyConfigId: taxonomy?.id,
    })
    if (!!taxonomy?.id && s === "") {
      updateStatus(false, taxonomy?.id)
    }
  }

  useEffect(() => {
    setContent(() => taxonomyContent)
  }, [taxonomyContent])

  return (
    <DG.Dialog
      open={open}
      onClose={onClose}
      id={id}
      className={styles.dialog}
      data-testid="external-id-modal"
    >
      <DG.DialogHeader className={styles.title}>
        <DG.DialogTitle id={id} className="HeadlineH5BlackBold">
          {t("agency.client.external-id.pattern.edit.title")}
        </DG.DialogTitle>
        <DG.DialogTitle
          id={id}
          className={cx("BodyBody-1BlackLight", styles.subtitle)}
        >
          {t("agency.client.external-id.pattern.edit.subtitle")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={onClose} />
      </DG.DialogHeader>
      <DG.DialogContent center>
        <div className={cx("BodyBody-1Primary-mainRegular", styles.info)}>
          <InfoIcon />
          <div className={styles.txt}>
            {t("agency.client.external-id.pattern.information")}
          </div>
        </div>
        {!!marketId && (
          <EditableInterpolatedText
            ref={textRef}
            value={content || ""}
            match={MATCH_RGX}
            options={
              !lineItemTaxonomy
                ? DYNAMIC_TAXONOMY_BO_OPTIONS
                : DYNAMIC_TAXONOMY_LI_OPTIONS
            }
            onBlur={handleBlur}
            onUpdate={handleChange}
            endAdornment={t("taxonomy.external-id-pattern.modal.unique-no")}
            clearAll={t("taxonomy.external-id-pattern.modal.clear-all")}
            addBtnText={t("taxonomy.add.dynamic.content")}
            noOptions={t("taxonomy.external-id-pattern.modal.no-options")}
            omitSelectedOptions={true}
            onClear={handleClear}
          />
        )}
      </DG.DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={onClose}
          className={styles.btn}
        >
          {t("external-id.dialog.cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onUpdate(content)}
        >
          {t("external-id.dialog.apply")}
        </Button>
      </DialogActions>
    </DG.Dialog>
  )
}

const styles = {
  dialog: css`
    *::-webkit-scrollbar {
      width: 6px;
      border-radius: 4px;
      background: var(--primary-light);
    }
    *::-webkit-scrollbar-thumb {
      width: 6px;
      height: var(--spacing-xl);
      border-radius: 4px;
      background: var(--primary-main);
    }
  `,
  title: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `,
  subtitle: css`
    padding-top: var(--spacing-xs);
  `,
  info: css`
    display: flex;
    align-items: center;
    padding: var(--spacing-s) var(--spacing-m);
    background-color: var(--secondary-extra-light-blue);
    border: 1px solid var(--secondary-dark-blue-2);
    border-radius: 4px;
    margin: var(--spacing-xs) 0px var(--spacing-l);
  `,
  txt: css`
    padding-left: var(--spacing-xs);
    color: var(--secondary-dark-blue-2);
  `,
  btn: css`
    margin-right: var(--spacing-m);
  `,
}
